import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  PrinterIcon,
  CloudArrowDownIcon,
  DocumentDuplicateIcon,
  ClipboardIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DentalTemplates() {
  const heroRef = useRef(null);
  const templatesRef = useRef(null);
  const featuresRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'Dental Consent Form Templates UK | Treatment-Specific & AI-Assisted | Smile Consent',
    'Dental treatment consent form templates for UK practices: AI-assisted drafting you edit before use, e-signatures & consent activity records. Faster than static PDFs—professional consent workflows in minutes.',
    'https://smileconsent.co.uk/dental-templates'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const templateCategories = [
    {
      category: 'General Dentistry',
      count: 45,
      description: 'Essential consent forms for routine dental procedures',
      templates: [
        'Examination & Diagnosis',
        'Preventive Treatment',
        'Restorative Treatment',
        'Periodontal Treatment',
        'Emergency Treatment'
      ],
      color: 'blue'
    },
    {
      category: 'Implant Dentistry',
      count: 32,
      description: 'Comprehensive consent forms for implant procedures',
      templates: [
        'Single Tooth Implant',
        'Multiple Implants',
        'Bone Grafting',
        'Sinus Lift',
        'Implant Maintenance'
      ],
      color: 'green'
    },
    {
      category: 'Orthodontics',
      count: 28,
      description: 'Specialized forms for orthodontic treatment',
      templates: [
        'Fixed Braces',
        'Invisalign Treatment',
        'Retainer Treatment',
        'Orthognathic Surgery',
        'Growth Modification'
      ],
      color: 'purple'
    },
    {
      category: 'Oral Surgery',
      count: 25,
      description: 'Consent forms for surgical procedures',
      templates: [
        'Simple Extraction',
        'Surgical Extraction',
        'Wisdom Tooth Removal',
        'Biopsy Procedures',
        'Cyst Removal'
      ],
      color: 'red'
    },
    {
      category: 'Cosmetic Dentistry',
      count: 22,
      description: 'Forms for aesthetic dental procedures',
      templates: [
        'Veneer Treatment',
        'Crown & Bridge',
        'Teeth Whitening',
        'Composite Bonding',
        'Smile Makeover'
      ],
      color: 'yellow'
    },
    {
      category: 'Endodontics',
      count: 18,
      description: 'Consent forms for root canal treatment',
      templates: [
        'Root Canal Treatment',
        'Retreatment',
        'Apicectomy',
        'Pulp Capping',
        'Vital Pulp Therapy'
      ],
      color: 'indigo'
    },
    {
      category: 'Paediatric Dentistry',
      count: 15,
      description: 'Specialized forms for children\'s dental treatment',
      templates: [
        'Child Examination',
        'Preventive Treatment',
        'Restorative Treatment',
        'Sedation Treatment',
        'Orthodontic Assessment'
      ],
      color: 'pink'
    },
    {
      category: 'Specialist Treatment',
      count: 15,
      description: 'Forms for specialist dental procedures',
      templates: [
        'Periodontal Surgery',
        'Oral Medicine',
        'Oral Pathology',
        'Dental Sedation',
        'Complex Rehabilitation'
      ],
      color: 'gray'
    }
  ];

  const templateFeatures = [
    {
      icon: ShieldCheckIcon,
      title: 'Clinician-reviewed drafts',
      description: 'AI-assisted wording is a starting point: your team edits and approves before anything is sent',
      benefits: [
        'Aligned with common UK consent themes',
        'You control the final record',
        'Supports consistent documentation',
        'Not a substitute for professional judgment'
      ]
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Fully Customizable',
      description: 'Review and edit any section before sending to match your practice and the patient',
      benefits: [
        'Practice branding',
        'Custom fields',
        'Logo integration',
        'Personalized content'
      ]
    },
    {
      icon: CloudArrowDownIcon,
      title: 'Instant Download',
      description: 'Send for digital signature; same PDF for clinic and patient, download when signed',
      benefits: [
        'PDF format',
        'Word documents',
        'Digital forms',
        'Print ready'
      ]
    },
    {
      icon: DocumentDuplicateIcon,
      title: 'Any procedure',
      description: 'Generate consent for all dental procedures—general, implant, ortho, surgery, cosmetic',
      benefits: [
        'Comprehensive coverage',
        'Regular additions',
        'Specialist forms',
        'Updated content'
      ]
    }
  ];

  const templateStats = [
    {
      metric: '< 1 min',
      description: 'Per consent generated',
      icon: '📄'
    },
    {
      metric: 'Edit',
      description: 'Every draft reviewed before send',
      icon: '✅'
    },
    {
      metric: '8',
      description: 'Specialist categories',
      icon: '🏥'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-purple-100 via-blue-100 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Dental consent form templates (UK)—treatment-specific & AI-assisted
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Go beyond static PDFs: AI-assisted drafting for every dental specialty, with e-signatures
              and consent activity records. Add diagnosis, treatment and cost—get editable UK-oriented wording in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <DocumentTextIcon className="h-6 w-6" />
                Start free trial
              </Link>
              <button
                type="button"
                onClick={() =>
                  templatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <PlayIcon className="h-6 w-6" />
                View Samples
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Template Stats Banner */}
      <section className="bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <DocumentTextIcon className="h-8 w-8" />
              <h2 className="text-2xl font-bold">AI-assisted consent for every specialty</h2>
            </div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              Generate drafts for any procedure—general dentistry, implants, orthodontics, surgery, cosmetic, and more.
              Your clinicians review and approve before send; UK-oriented wording only.
            </p>
          </div>
        </div>
      </section>

      {/* Procedure categories — hero “View Samples” scrolls here */}
      <section
        ref={templatesRef}
        id="consent-samples"
        className="py-20 bg-white scroll-mt-24"
        aria-label="Treatment category samples"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Treatment categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Generate consent for any of these procedure types. Add diagnosis, treatment, tooth and cost—AI does the rest.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {templateCategories.map((category, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{category.category}</h3>
                  <div className={`bg-${category.color}-100 text-${category.color}-800 px-2 py-1 rounded-full text-xs font-bold`}>
                    {category.count}
                  </div>
                </div>
                <p className="text-gray-600 mb-4 text-sm">{category.description}</p>
                <div className="space-y-2">
                  {category.templates.map((template, templateIndex) => (
                    <div key={templateIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{template}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Link to="/register" className="block w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                    <EyeIcon className="h-4 w-4" />
                    Generate consent
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why use AI-generated consent?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              UK-law ready wording, full compliance, and generated in under a minute for any treatment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {templateFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Consent coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Generate consent for all dental procedures and specialties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {templateStats.map((stat, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl animate-on-scroll text-center">
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.metric}</div>
                <p className="text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-500 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to generate consent in under a minute?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of dental practices using AI-generated consent. 
            Start your free trial today—no downloads, just generate and send.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-plans"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <ChartBarIcon className="h-6 w-6" />
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                AI-generated dental consent and consent management for UK practices.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}
